/**
 * L'annuaire des clients, vu par l'écran.
 *
 * Ce fichier ne décrit que ce qui distingue un client : ses champs, les mots
 * qui le nomment, les deux phrases dont le sens lui est propre. Tout le reste —
 * le filtrage, la validation, la différence, le code proposé — est dans
 * `annuaire.ts`, à un seul endroit pour tous les annuaires.
 *
 * Miroir de `ANNUAIRE_CLIENTS` dans `apps/api/src/clients.ts`, `actif` en
 * moins : il se change par les boutons Désactiver et Réactiver, jamais par le
 * formulaire. **Les longueurs maximales sont celles de l'API**, recopiées ici
 * pour que la personne soit prévenue avant l'envoi plutôt qu'après le refus.
 * Si les deux divergent un jour, c'est l'API qui a raison : elle refusera, et
 * l'écran affichera son refus.
 *
 * L'ordre des champs est celui où l'écran les pose, et les `court` y vont
 * **par paires** : code et SIRET, TVA et pays, code postal et ville, téléphone
 * et courriel, conditions et encours. Un `court` isolé laisserait un trou à sa
 * droite — constaté à l'écran le 03/09/2026.
 */

import type { Annuaire } from './annuaire.js';

export const ANNUAIRE_CLIENTS: Annuaire = {
  chemin: 'clients',
  cleListe: 'clients',
  cleFiche: 'client',
  singulier: 'client',
  pluriel: 'clients',
  // Un client ne roule pas : ni matériel, ni zones desservies.
  couverture: false,
  conditions: true,
  noteConditions:
    'L’encours autorisé est enregistré, mais aucune règle ne s’appuie encore dessus : il ne bloque rien aujourd’hui.',
  noteDesactive:
    'Il reste consultable, ainsi que les transports déjà réalisés pour lui.',
  champs: [
    { cle: 'nom', etiquette: 'Nom', max: 200, obligatoire: true },
    { cle: 'code', etiquette: 'Code', max: 32, chiffre: true, court: true },
    { cle: 'siret', etiquette: 'SIRET', max: 14, chiffre: true, court: true },
    // LE SIREN EST À CÔTÉ DU SIRET, et c'est délibéré : les neuf premiers
    // chiffres d'un SIRET SONT le SIREN, et les voir l'un sous l'autre est ce
    // qui permet de repérer une incohérence. Aucune règle ne les lie — on
    // connaît souvent le SIREN sans savoir de quel établissement on commande.
    { cle: 'siren', etiquette: 'SIREN', max: 9, chiffre: true, court: true },
    { cle: 'tva', etiquette: 'N° de TVA', max: 32, chiffre: true, court: true },
    // PLEINE LARGEUR DEPUIS LE 09/09/2026, et c'est une CORRECTION, pas un
    // goût : l'ajout du SIREN a fait passer cette série de champs courts de
    // quatre à cinq, et « Pays » se retrouvait seul sur sa rangée avec un trou
    // à sa droite. MESURÉ sur l'aperçu. C'est le défaut que l'en-tête de ce
    // fichier dit avoir corrigé le 03/09 ; il fallait le corriger à nouveau.
    // Un déroulant de noms de pays s'accommode très bien de la pleine largeur.
    { cle: 'pays', etiquette: 'Pays', max: 80, liste: 'pays' },
    { cle: 'adresse', etiquette: 'Adresse', max: 500 },
    {
      cle: 'code_postal',
      etiquette: 'Code postal',
      max: 16,
      chiffre: true,
      court: true,
    },
    { cle: 'ville', etiquette: 'Ville', max: 120, court: true },
    { cle: 'telephone', etiquette: 'Téléphone', max: 40, chiffre: true, court: true },
    { cle: 'courriel', etiquette: 'Courriel', max: 200, court: true },
    { cle: 'site_web', etiquette: 'Site', max: 200 },
    // Même correction, même cause : la fréquence a rendu cette série impaire et
    // « Encours autorisé » tombait seul. « 45 jours fin de mois » remplit une
    // pleine largeur sans qu'elle paraisse vide.
    {
      cle: 'conditions_paiement',
      etiquette: 'Conditions de paiement',
      max: 200,
      liste: 'conditions',
    },
    // LA FRÉQUENCE DE FACTURATION, 09/09/2026. Juste après les conditions de
    // paiement : les deux forment l'accord commercial, et les séparer
    // obligerait à chercher. Elle décide combien de factures naissent d'un même
    // client dans l'écran « À facturer ».
    {
      cle: 'frequence_facturation',
      etiquette: 'Fréquence de facturation',
      max: 16,
      court: true,
      liste: 'frequences',
    },
    {
      cle: 'encours_autorise',
      etiquette: 'Encours autorisé',
      max: 20,
      chiffre: true,
      court: true,
    },
    // L'ADRESSE D'ENVOI DES FACTURES, 09/09/2026. Elle ferme le bloc des
    // conditions commerciales : conditions de paiement, fréquence, encours, et
    // où l'on envoie.
    //
    // DISTINCTE DE « Courriel » plus haut, qui est celui de l'exploitation.
    // Chez un chargeur, la comptabilité fournisseurs a presque toujours sa
    // propre boîte, et une facture envoyée à l'exploitation disparaît.
    //
    // PLEINE LARGEUR, pour deux raisons qui vont dans le même sens : une
    // adresse de courriel est longue et se tronque mal dans une demi-colonne,
    // et la mettre en `court` rendrait la série impaire — « Encours autorisé »
    // retomberait seul avec un trou à sa droite, exactement le défaut corrigé
    // deux fois aujourd'hui.
    {
      cle: 'courriel_facturation',
      etiquette: 'Courriel d’envoi des factures',
      max: 200,
    },
    { cle: 'note', etiquette: 'Note', max: 2000, long: true },
  ],
};
